import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { fetchAllUserEvents } from "../../api/userEvents";
import { useState, useMemo, useEffect } from "react";

export function useUserEventsQuery({ limit = 10 } = {}) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const token = auth?.user?.access_token ?? null;
  const isAuthenticated = auth?.isAuthenticated;
  const logoutInProgress =
    typeof window !== "undefined" &&
    sessionStorage.getItem("logoutInProgress") === "1";

  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [committedSearch, setCommittedSearch] = useState("");
  const [isReady, setIsReady] = useState(false);

  // Clear cache first, then allow the query to run
  useEffect(() => {
    queryClient.removeQueries({ queryKey: ["user-events-all"] });
    setIsReady(true);
  }, [queryClient]);

  const query = useQuery({
    queryKey: ["user-events-all"],
    queryFn: () => fetchAllUserEvents(token),
    enabled: Boolean(isAuthenticated && token && !logoutInProgress && isReady),
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error.message === "unauthorized") return false;
      return failureCount < 2;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const allItems = useMemo(() => query.data ?? [], [query.data]);

  const filtered = useMemo(() => {
    if (!committedSearch) return allItems;
    const lower = committedSearch.toLowerCase();
    return allItems.filter((item) =>
      (item.company ?? "").toLowerCase().includes(lower),
    );
  }, [allItems, committedSearch]);

  const totalPages = Math.ceil(filtered.length / limit);
  const safePage = Math.min(page, Math.max(totalPages - 1, 0));
  const pageItems = filtered.slice(safePage * limit, (safePage + 1) * limit);

  const goToPage = (next) => {
    if (next < 0 || (totalPages > 0 && next >= totalPages)) return;
    setPage(next);
  };

  const onSearchChange = (val) => setSearch(val);

  const onSearchSubmit = () => {
    setPage(0);
    setCommittedSearch(search);
  };

  const onSearchClear = () => {
    setSearch("");
    setCommittedSearch("");
    setPage(0);
  };

  const refreshUserEvents = async () => {
    setPage(0);
    setSearch("");
    setCommittedSearch("");
    await queryClient.invalidateQueries({ queryKey: ["user-events-all"] });
  };

  return {
    ...query,
    data: { items: pageItems },
    page: safePage,
    setPage: goToPage,
    limit,
    hasNextPage: safePage < totalPages - 1,
    hasPrevPage: safePage > 0,
    search,
    committedSearch,
    onSearchChange,
    onSearchSubmit,
    onSearchClear,
    refreshUserEvents,
  };
}
